import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    NavigationEnd,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import {
    AppStateFacade,
    UserAuthState,
} from '@app-state';
import {
    FOLDER_ID_PARAM,
    TENANCY_ID_PARAM,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { restrictedUrlTree } from '@pages/shared/restricted-access';
import { AuthService } from '@services';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';

import {
    filter,
    map,
    take,
} from 'rxjs/operators';

@Injectable()
export class FolderParamGuard implements CanActivate {
    constructor(
        private _appState: AppStateFacade,
        private _router: Router,
        private _snack: UiSnackBarService,
        private _translateService: TranslateService,
        private _authService: AuthService,
    ) { }

    canActivate(
        _: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ) {
        if (this._appState.user.authState !== UserAuthState.LoggedInAsTenant) {
            return true;
        }

        const paramMap = state.root.queryParamMap;

        const urlFolder = parseNumberOrNull(paramMap.get(FOLDER_ID_PARAM));
        const urlTenancy = parseNumberOrNull(paramMap.get(TENANCY_ID_PARAM));

        const currentUserFolder = this._appState.user.folder.value!.id;
        const currentUserTenancy = this._appState.user.value!.tenancyId;

        if (urlTenancy !== null && currentUserTenancy !== urlTenancy) {
            return this._redirect();
        }

        if (urlFolder !== null && currentUserFolder !== urlFolder) {
            return this._appState.user.folder.set(urlFolder)
                .pipe(
                    map(success => success || this._redirect()),
                );
        }

        // FIXME: ???
        this._router.events.pipe(
            filter(ev => ev instanceof NavigationEnd),
            filter(() => this._appState.user.authState === UserAuthState.LoggedInAsTenant),
            filter(() => !this._authService.pendingLogout$.value),
            take(1),
        ).subscribe(() => {
            if (
                urlFolder == null &&
                urlTenancy == null &&
                this._appState.user.folder.hasAny
            ) {
                this._router.navigate([state.url.split('?')[0]], {
                    queryParams: {
                        ...state.root.queryParams,
                        [FOLDER_ID_PARAM]: currentUserFolder,
                        [TENANCY_ID_PARAM]: currentUserTenancy,
                    },
                    preserveFragment: true,
                    replaceUrl: true,
                });
            }
        });

        return true;
    }

    private _redirect() {
        this._snack.error(
            this._translateService.instant('snack_ou_access_denied'),
        );
        return restrictedUrlTree(this._router);
    }
}

function parseNumberOrNull(param: string | null) {
    if (param === null) {
        return null;
    }
    return Number(param);
}
